import {Component, inject, Inject, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {AppointmentType, Appointment, CalendarAppointment, QueryPatient, AppointmentMetadata, instantToPracticeWallDate, pickerToWallClock, PracticeTimezoneService} from '@xmedical/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {DatePipe} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {XmedicalUiModule} from '@xmedical/ui';
import {AppointmentPatientMetaDataFormComponent, AppointmentPatientMetaDataFormDialogData} from '../appointment-patient-meta-data-form/appointment-patient-meta-data-form.component';
import {CALENDAR_ACTION_HANDLER, CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';
import dayjs from 'dayjs/esm';

function endAfterStartValidator(control: AbstractControl): ValidationErrors | null {
  const start = control.get('date')?.value;
  const end = control.get('endDate')?.value;
  if (!start || !end) return null;

  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!startDate.isValid() || !endDate.isValid()) return null;

  if (endDate.valueOf() < startDate.valueOf()) {
    return {endBeforeStart: true};
  }
  return null;
}

export interface NewAppointmentDialogData {
  appointmentTypes: AppointmentType[];
  selectedDate: Date;
  selectedEndDate?: Date;
  newAppointment: Appointment;
  intervals: Date[];
  practiceLocationId?: number;
  practicePractitionerId?: number;
}

@Component({
  selector: 'app-new-appointment',
  templateUrl: './new-appointment.component.html',
  styleUrls: ['./new-appointment.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    XmedicalUiModule,
  ],
  providers: [DatePipe]
})
export class NewAppointmentComponent implements OnInit, OnDestroy {

  validate = false;
  form: UntypedFormGroup;
  selectedDate: Date = new Date();
  appointmentTypes: AppointmentType[] = [];
  patient?: QueryPatient = null;
  selectedType: AppointmentType = null;
  saving = false;

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private actionHandler = inject(CALENDAR_ACTION_HANDLER);
  private practiceTz = inject(PracticeTimezoneService);

  private appointmentsRepositorySubscription: Subscription;
  private metaFormDialogSubscription: Subscription;
  private addPatientDialogSubscription: Subscription;
  private formSubscription: Subscription;
  private dateChangeSubscription: Subscription;

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<NewAppointmentComponent>,
    @Inject(MAT_DIALOG_DATA) private data: NewAppointmentDialogData,
    public datePipe: DatePipe,
    private formBuilder: UntypedFormBuilder,
  ) {
  }

  get allowPatientAppointmentsWithoutPatient(): boolean {
    return this.dataProvider.practiceSettings()?.allowPatientAppointmentsWithoutPatient ?? false;
  }

  get practiceSettings() {
    return this.dataProvider.practiceSettings();
  }

  get formValid(): boolean {
    if (!this.form) {
      return false;
    }
    return this.form.valid || false;
  }

  get hasPatient(): boolean {
    return this.patient !== null;
  }

  get hasPatientMetaData(): boolean {
    return this.appointmentMetaData.value.name !== '';
  }

  get appointmentMetaData(): UntypedFormGroup {
    return this.form.get('appointmentMetaData') as UntypedFormGroup;
  }

  get appointmentMetaPatientData(): AppointmentMetadata {
    return this.appointmentMetaData.value as AppointmentMetadata;
  }

  ngOnInit(): void {
    // selectedDate / selectedEndDate arrive as true instants (from the calendar
    // grid, day-view slot, or FAB). Convert to practice wall-clock so the
    // timezone-unaware datetime picker shows practice time; on save we send that
    // wall-clock verbatim (pickerToWallClock). For an in-zone viewer these are no-ops.
    const zone = this.practiceTz.practiceTimezone();
    this.selectedDate = instantToPracticeWallDate(this.data.selectedDate, zone);
    this.appointmentTypes = (this.data.appointmentTypes ?? []).filter(t => t.canCreate !== false);
    this.selectedType = this.appointmentTypes[0] ?? null;

    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = this.data.selectedEndDate
      ? instantToPracticeWallDate(this.data.selectedEndDate, zone)
      : new Date(this.selectedDate.getTime() + defaultDuration * 60 * 1000);

    this.form = this.formBuilder.group({
      title: [this.selectedType?.title ?? '', Validators.maxLength(100)],
      patientId: [''],
      date: [this.selectedDate, Validators.required],
      endDate: [endDate, Validators.required],
      comment: ['', Validators.maxLength(250)],
      appointmentType: [this.selectedType?.id, Validators.required],
      appointmentMetaData: this.formBuilder.group({
        name: [''],
        cell: [''],
        email: [''],
      })
    }, {validators: endAfterStartValidator});
    this.formSubscription = this.form.get('appointmentType').valueChanges.subscribe(val => {
      this.selectedType = this.appointmentTypes.find(t => t.id === val) ?? null;
      this.form.get('title').setValue(this.selectedType?.title ?? '');
    });

    // When start changes, shift end to maintain duration
    this.dateChangeSubscription = this.form.get('date').valueChanges.subscribe(newStart => {
      if (!newStart) return;
      const currentStart = dayjs(this.selectedDate);
      const currentEnd = dayjs(this.form.get('endDate').value);
      if (currentStart.isValid() && currentEnd.isValid()) {
        const durationMs = currentEnd.valueOf() - currentStart.valueOf();
        this.form.get('endDate').setValue(dayjs(newStart).add(durationMs, 'millisecond').toDate(), {emitEvent: false});
      }
      this.selectedDate = dayjs(newStart).toDate();
    });
  }

  ngOnDestroy(): void {
    this.appointmentsRepositorySubscription?.unsubscribe();
    this.metaFormDialogSubscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.dateChangeSubscription?.unsubscribe();
    this.addPatientDialogSubscription?.unsubscribe();
  }

  openMetaForm() {
    const dialogRef = this.dialog.open(AppointmentPatientMetaDataFormComponent, {
      disableClose: true,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: this.appointmentMetaData.value
    });
    this.metaFormDialogSubscription = dialogRef.afterClosed().subscribe((formData: AppointmentPatientMetaDataFormDialogData) => {
      if (formData) {
        this.appointmentMetaData.patchValue(formData);
        this.form.get('title').setValue(formData.name);
      }
    });
  }

  save() {
    if (this.form.valid) {
      this.saving = true;
      const appointmentDetails = this.form.value;
      // Appointment times are zone-less wall-clock; send the wall the user picked
      // verbatim (never a converted instant).
      const appointment = {
        title: appointmentDetails.title,
        start: pickerToWallClock(appointmentDetails.date),
        end: pickerToWallClock(appointmentDetails.endDate),
        comment: appointmentDetails.comment,
        patientId: this.patient?.id,
        practiceId: this.dataProvider.selectedPracticeId(),
        practiceLocationId: this.data.practiceLocationId,
        practicePractitionerId: this.data.practicePractitionerId,
        deleted: false,
        appointmentType: this.selectedType,
        appointmentMetaData: {
          name: appointmentDetails.appointmentMetaData.name,
          cell: appointmentDetails.appointmentMetaData.cell,
          email: appointmentDetails.appointmentMetaData.email,
        }
      } as Appointment;

      this.appointmentsRepositorySubscription = this.dataProvider.createAppointment(appointment).subscribe({
        next: () => {
          this.saving = false;
          this.dialogRef.close(appointment);
        },
        error: (error) => {
          console.error(error);
          this.saving = false;
        }
      });
    }
    this.validate = true;
  }

  close() {
    this.dialogRef.close();
  }

  searchPatients = (query: string): Observable<QueryPatient[]> => {
    return this.dataProvider.searchPatients(query.toLowerCase());
  }

  addNewPatient() {
    const tempAppointment = {
      metaData: this.appointmentMetaData.value
    } as CalendarAppointment;
    this.addPatientDialogSubscription = this.actionHandler.onCreatePatientRequested(tempAppointment).subscribe((patient: QueryPatient) => {
      if (patient) {
        this.patientSelected(patient);
      }
    });
  }

  clearPatient() {
    this.patient = null;
    this.form.get('patientId').setValue(null);
    this.form.get('patientId').updateValueAndValidity();
    this.appointmentMetaData.reset();
  }

  patientSelected(patient: QueryPatient) {
    this.patient = patient;
    this.form.get('patientId').setValue(patient.id);
    this.form.get('patientId').updateValueAndValidity();
    this.form.get('title').setValue(patient.formalName ?? patient.name ?? this.form.get('title').value);
    this.form.get('title').updateValueAndValidity();
  }
}
